import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useFormState } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSTextField, RMSSelect } from '../../../shared/components/form-controls';
import RMSRecordHistoryButton from '../../../shared/components/modules/RMSRecordHistoryButton';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import CreateFormResolver from '../../../shared/utility/createFormResolver';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import { savePersonTag } from '../../services/userService';
import personTagSchema from '../../schema/personTagSchema';
import { DICT_SELECT_PROPS, TAG_PARENT_TYPE } from '../../constants/personTagFormConstants';

const PersonTagDialog = ({ open, onClose, personId, tag = null, existingTags = [], onSaved }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.userData.user);
  const [submitting, setSubmitting] = useState(false);

  const methods = useForm({
    resolver: CreateFormResolver(personTagSchema),
    mode: 'onSubmit',
    defaultValues: { Type: tag?.Type || '', Value: tag?.Value || '' },
  });

  const { isDirty } = useFormState({ control: methods.control });

  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const { tagTypeOptions } = useUserDictionaryOptions();
  const isEditMode = Boolean(tag?.ptsTagID);

  const availableTagTypes = useMemo(() => {
    const usedTypes = new Set(
      existingTags
        .filter((existingTag) => !existingTag.IsDeleted && existingTag.Type !== tag?.Type)
        .map((existingTag) => existingTag.Type)
    );
    return tagTypeOptions.filter(
      (option) => option.Parent === TAG_PARENT_TYPE && !usedTypes.has(option.Code)
    );
  }, [existingTags, tagTypeOptions, tag]);

  const onSubmit = useCallback(
    async (data) => {
      const payload = {
        ParentType: TAG_PARENT_TYPE,
        ptsParentID: personId,
        Type: data.Type,
        Value: data.Value || null,
        IsLocked: false,
        IsSealed: false,
        IsDeleted: false,
        UpdatedBy: currentUser?.Username,
      };

      if (isEditMode) {
        payload.ptsTagID = tag.ptsTagID;
      } else {
        payload.CreatedBy = currentUser?.Username;
      }

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () => savePersonTag(payload),
        onSuccess: () => {
          dispatch(
            notify(isEditMode ? 'Tag updated successfully' : 'Tag created successfully', 'success')
          );
          onSaved?.();
        },
      });
    },
    [personId, currentUser, isEditMode, tag, dispatch, onSaved]
  );

  const handleSave = useCallback(() => methods.handleSubmit(onSubmit)(), [methods, onSubmit]);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this tag. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  return (
    <RMSCommonDialog
      open={open}
      title={isEditMode ? 'Edit Tag' : 'Add Tag'}
      onClose={requestClose}
      onSave={handleSave}
      loading={submitting}
      saveDisabled={submitting}
      maxWidth="sm"
      fullWidth
      closeOnBackdropClick={false}
      saveButton={{
        show: true,
        type: 'simple',
        label: 'Save',
        variant: 'contained',
        color: 'primary',
      }}
      cancelButton={{
        show: true,
        type: 'simple',
        label: 'Close',
        variant: 'outlined',
        color: 'secondary',
      }}
      leftActions={
        isEditMode && (
          <RMSRecordHistoryButton
            disabled={!tag?.Created}
            created={tag?.Created}
            createdBy={tag?.CreatedBy}
            updated={tag?.Updated}
            updatedBy={tag?.UpdatedBy}
          />
        )
      }>
      <FormProvider {...methods}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <RMSSelect
              name="Type"
              label="Tag Type"
              options={availableTagTypes}
              disabled={isEditMode}
              {...DICT_SELECT_PROPS}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSTextField name="Value" label="Description" inputProps={{ maxLength: 100 }} />
          </Grid>
        </Grid>
      </FormProvider>

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </RMSCommonDialog>
  );
};

export default PersonTagDialog;
